/* __next_internal_client_entry_do_not_use__ Wrapper,Modal,MobileHeaderImage,RegularSideImage auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { optimizeImageUrlHeight, optimizeImageUrlWidth } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React, { useState } from "react";
import styled, { useTheme } from "styled-components";
import { BaseModal } from "./BaseModal";
import { useBackgroundScrollBlocking } from "./hooks/useBackgroundScrollBlocking";
import { useModalScrolling } from "./hooks/useModalScrolling";
import { useModalStyle } from "./hooks/useModalStyle";
import { useTrackModalEvent } from "./hooks/useTrackModalEvent";
import { ModalNavigationAbsolute } from "./ModalNavigation/ModalNavigationAbsolute";
import { ModalNavigationFixed } from "./ModalNavigation/ModalNavigationFixed";
/**
 * Default wrapping div for the children of the `Modal`.
 * Exported so that modifications can be made to this for
 * cases where smaller content can be centered by wrapping
 * it in a parent component.
 *
 * @trackcomponent
 */ export var Wrapper = styled.div.withConfig({
    componentId: "sc-e3ec0035-0"
})([
    "display:flex;flex-direction:column;align-items:",
    ";justify-content:",
    ";overflow:inherit;height:100%;max-height:",
    ";width:100%;box-sizing:border-box;padding:",
    ";",
    ";"
], function(param) {
    var $align = param.$align;
    return $align;
}, function(param) {
    var $justify = param.$justify;
    return $justify;
}, function(param) {
    var $isMobile = param.$isMobile;
    return $isMobile ? "inherit" : "80vh";
}, function(param) {
    var $padding = param.$padding;
    return $padding;
}, function(param) {
    var $hasSideImage = param.$hasSideImage;
    return $hasSideImage && "padding-left: 50%";
});
// Image sizing
var DESKTOP_IMAGE_MAX_INLINE_SIZE = 375;
var MOBILE_IMAGE_BLOCK_SIZE = 168;
/**
 * @link [Docs](https://slate.faire.team/slate/component/modal)
 * @trackcomponent
 */ export var Modal = function(param) {
    var primary = param.primary, padding = param.padding, children = param.children, onClose = param.onClose, _param_closeIconProps = param.closeIconProps, closeIconProps = _param_closeIconProps === void 0 ? {} : _param_closeIconProps, _param_showCloseButton = param.showCloseButton, showCloseButton = _param_showCloseButton === void 0 ? true : _param_showCloseButton, closeButtonVariant = param.closeButtonVariant, onBack = param.onBack, _param_backIconProps = param.backIconProps, backIconProps = _param_backIconProps === void 0 ? {} : _param_backIconProps, onForward = param.onForward, _param_forwardIconProps = param.forwardIconProps, forwardIconProps = _param_forwardIconProps === void 0 ? {} : _param_forwardIconProps, _param_stickyNav = param.stickyNav, stickyNav = _param_stickyNav === void 0 ? false : _param_stickyNav, width = param.width, height = param.height, className = param.className, parentElement = param.parentElement, contentStyle = param.contentStyle, overlayStyle = param.overlayStyle, shouldReturnFocusAfterClose = param.shouldReturnFocusAfterClose, shouldCloseOnOverlayClick = param.shouldCloseOnOverlayClick, _param_justify = param.justify, justify = _param_justify === void 0 ? "flex-start" : _param_justify, _param_align = param.align, align = _param_align === void 0 ? "center" : _param_align, ariaHideApp = param.ariaHideApp, dataTestId = param["data-test-id"], setContentRef = param.setContentRef, postEventModalId = param.postEventModalId, _param_isOpen = param.isOpen, isOpen = _param_isOpen === void 0 ? true : _param_isOpen, allowBackgroundScroll = param.allowBackgroundScroll, noPortal = param.noPortal, ariaLabelledBy = param["aria-labelledby"], ariaDescribedBy = param["aria-describedby"], image = param.image, imageAlt = param.imageAlt;
    var isSSR = useIsSSR();
    useTrackModalEvent(isOpen, postEventModalId);
    var _useBackgroundScrollBlocking = useBackgroundScrollBlocking(allowBackgroundScroll), handleAfterOpen = _useBackgroundScrollBlocking.handleAfterOpen, handleAfterClose = _useBackgroundScrollBlocking.handleAfterClose;
    var isMobile = useViewport().isMobile;
    var _useModalScrolling = useModalScrolling(!!image), showDivider = _useModalScrolling.showDivider, navigationOpacity = _useModalScrolling.navigationOpacity, refCallBack = _useModalScrolling.refCallBack;
    var _useState = _sliced_to_array(useState(false), 2), imageError = _useState[0], setImageError = _useState[1];
    var onImageError = function() {
        if (!imageError) {
            setImageError(true);
        }
    };
    var theme = useTheme();
    var nestedTheme = _object_spread_props(_object_spread({}, theme), {
        buttons: theme.buttons
    });
    var hasSideImage = !isMobile && !!image && !imageError;
    var hasHeaderImage = !!image && isMobile && !imageError;
    var hasImage = hasSideImage || hasHeaderImage;
    var navProps = {
        onClose: onClose,
        onBack: onBack,
        onForward: onForward,
        primary: primary,
        showCloseButton: showCloseButton,
        closeButtonVariant: closeButtonVariant,
        closeIconProps: closeIconProps,
        backIconProps: backIconProps,
        forwardIconProps: forwardIconProps,
        showDivider: showDivider,
        hasImage: hasImage,
        isMobile: isMobile,
        navigationOpacity: navigationOpacity
    };
    var modalStyle = useModalStyle(hasImage, height, width, contentStyle, noPortal, overlayStyle);
    return /*#__PURE__*/ _jsx(ThemeProvider, {
        theme: nestedTheme,
        children: /*#__PURE__*/ _jsx(BaseModal, {
            contentRef: setContentRef,
            ariaHideApp: ariaHideApp,
            primary: primary,
            className: className,
            isOpen: isOpen,
            shouldReturnFocusAfterClose: shouldReturnFocusAfterClose,
            onAfterClose: handleAfterClose,
            onAfterOpen: handleAfterOpen,
            parentSelector: parentElement === undefined ? undefined : function() {
                return parentElement;
            },
            style: modalStyle,
            onRequestClose: onClose,
            "data-test-id": "faireModal",
            shouldCloseOnOverlayClick: shouldCloseOnOverlayClick,
            noPortal: noPortal,
            aria: {
                labelledby: ariaLabelledBy,
                describedby: ariaDescribedBy
            },
            children: /*#__PURE__*/ _jsxs(Wrapper, {
                onClick: function(e) {
                    return e.stopPropagation();
                },
                "data-test-id": dataTestId,
                $padding: padding !== null && padding !== void 0 ? padding : isMobile ? "64px 12px" : "30px 57px",
                $justify: justify,
                $align: align,
                ref: refCallBack,
                $hasSideImage: hasSideImage,
                $isMobile: isMobile,
                children: [
                    hasSideImage && /*#__PURE__*/ _jsx(RegularSideImage, {
                        alt: imageAlt || image.alt_text || "",
                        onError: onImageError,
                        src: optimizeImageUrlWidth(image, isSSR, DESKTOP_IMAGE_MAX_INLINE_SIZE)
                    }),
                    stickyNav ? /*#__PURE__*/ _jsx(ModalNavigationFixed, _object_spread({}, navProps)) : /*#__PURE__*/ _jsx(ModalNavigationAbsolute, _object_spread({}, navProps)),
                    hasHeaderImage && /*#__PURE__*/ _jsx(MobileHeaderImage, {
                        alt: imageAlt || image.alt_text || "",
                        onError: onImageError,
                        src: optimizeImageUrlHeight(image, isSSR, MOBILE_IMAGE_BLOCK_SIZE)
                    }),
                    children
                ]
            })
        })
    });
};
export var MobileHeaderImage = styled.img.withConfig({
    componentId: "sc-e3ec0035-1"
})([
    "block-size:",
    "px;inline-size:100%;object-fit:cover;"
], MOBILE_IMAGE_BLOCK_SIZE);
export var RegularSideImage = styled.img.withConfig({
    componentId: "sc-e3ec0035-2"
})([
    "block-size:100%;inline-size:50%;position:absolute;inset-block-start:0;inset-inline-start:0;object-fit:cover;"
]);
